import axios from 'axios'
import { useMembershipStore } from '~/stores/membership'
import { useGoogleAnalyticsDownloadEvent } from '~/composables/google-analytics'
import { logError } from '@/utils/log-error'
import type { NuxtApp } from '#app'
import type { TCommon } from '~/types/app.type'

export const MODULES = {
  SPECIAL_ISSUES: 'special-issues',
  PUBLICATIONS: 'publications',
}

export function useDownloadFile({ module = MODULES.PUBLICATIONS } = {}) {
  const authStore = useAuthStore()
  const { $token, $config, $modal, $rudderstack }: NuxtApp = useNuxtApp()
  const membershipStore = useMembershipStore()

  const downloadProgress = ref(0)
  const isDownloading = ref(false)
  const finishDownload = ref(false)
  let abortController

  async function getFile(id: string, category: string): Promise<any> {
    const path = `/downloads/category/${category}/${id}`
    const baseUrl = $config.public.apiRestEndpoint
    const url = `${baseUrl || $config.public.baseUrl}${path}`
    const headers: Record<string, any> = await $token.getAuthorizationHeader()

    abortController = new AbortController()

    return axios({
      method: 'post',
      url,
      responseType: 'blob',
      headers: headers.headers,
      signal: abortController.signal,
      onDownloadProgress: (progressEvent) => {
        finishDownload.value = false
        const total = parseFloat(String(progressEvent.total))
        const current = progressEvent.loaded
        setDownloadProgress(Math.floor((current / total) * 100))
      },
    }).finally(resetDownloadState)
  }

  function setDownloadProgress(progress: number) {
    if (!isDownloading.value && abortController === undefined) return
    downloadProgress.value = progress
  }

  function resetDownloadState() {
    isDownloading.value = false
    downloadProgress.value = 0
    finishDownload.value = true
    abortController = undefined
  }

  function cancelDownload() {
    if (!abortController) return

    abortController.abort()
    resetDownloadState()
  }

  async function downloadFile(
    id: string,
    category: string,
    fileName: string,
    subscriptionRequired?: string,
  ) {
    isDownloading.value = true
    let blob: Blob

    try {
      blob = (await getFile(id, category)).data
      useGoogleAnalyticsDownloadEvent(fileName)
    } catch {
      isDownloading.value = false
      return
    }

    const ext = fileName.split('.').pop()
    $rudderstack.trackDownloads({
      file_id: id,
      file_name: fileName,
      file_type: ext,
      file_format: ext,
      subscription_required: subscriptionRequired,
    })

    const blobUrl = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.style.display = 'none'
    a.href = blobUrl
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(blobUrl)
    isDownloading.value = false
  }

  function onClickDownload(
    id: string,
    category: string,
    fileName: string,
    subscriptionRequired?: string,
  ) {
    if (downloadProgress.value || isDownloading.value) return cancelDownload()

    const fileExtension = fileName.split('.').pop()?.toLowerCase()

    const validExtensions = ['doc', 'docx', 'dotx', 'pdf', 'xls', 'xlsx']

    if (!fileExtension || !validExtensions.includes(fileExtension)) {
      logError('Invalid file extension or missing file extension.')
      return
    }

    downloadFile(id, category, fileName, subscriptionRequired)
  }

  const isAuthenticated = computed(() => authStore.isAuthenticated)

  const callToAction = computed(() => {
    const isSpecialIssues = module === MODULES.SPECIAL_ISSUES
    const hasSpecialIssuesSub = membershipStore.isSpecialIssuesSubActive

    if (!isAuthenticated.value)
      return {
        text: 'Login',
        callback: () => {
          $modal.open('login')
        },
      }

    // Special Issues is the only publications that has a dedicated subscription
    // called "Special Issues"
    if (isSpecialIssues) {
      if (!hasSpecialIssuesSub && !membershipStore.isSubscriberActive) {
        return {
          text: 'Subscribe',
          callback: () => {
            $modal.open('requestATrial')
          },
        }
      }
    } else {
      if (!membershipStore.isSubscriberActive)
        return {
          text: 'Subscribe',
          callback: () => {
            $modal.open('requestATrial')
          },
        }
    }

    return {
      text: 'Download',
      callback: onClickDownload,
    }
  })

  return {
    callToAction,
    isDownloading,
    downloadProgress,
    cancelDownload,
    finishDownload,
  }
}
